import { PropsWithChildren, ReactNode } from "react"

import {
  Combobox,
  ComboboxProps,
  DefaultEmptyState,
  highlightedRowClassName,
} from "./Combobox"
import { useMultiCombobox } from "./useCombobox"
import { CheckboxLabelled } from "../../base/control/CheckboxLabelled"

export interface MultiComboboxProps<T> {
  selectedItemKeys: Set<string>
  items: T[]
  keyExtractor: (item: T) => string
  valueExtractor: (item: T) => string
  contentExtractor?: (item: T) => ReactNode
  onSelect: (item: T) => void
  autoFilter?: boolean
  rowClassName?: string
  comboboxProps?: Partial<ComboboxProps<T>>
  onChangeQuery?: (query: string) => void
  onLoadMore?: () => void
  maxRenderedItems?: number
  inline?: boolean
}

export function MultiCombobox<T>({
  items,
  keyExtractor,
  valueExtractor,
  contentExtractor,
  onSelect,
  selectedItemKeys,
  autoFilter,
  rowClassName,
  comboboxProps,
  onChangeQuery,
  onLoadMore,
  maxRenderedItems,
  inline,
}: MultiComboboxProps<T>) {
  const useComboboxRes = useMultiCombobox({
    items,
    keyExtractor,
    valueExtractor,
    onSelect,
    autoFilter,
    onChangeQuery,
    maxRenderedItems,
    inline,
  })

  return (
    <Combobox
      useComboboxRes={useComboboxRes}
      renderItem={(item, isHighlighted) => (
        <div className={highlightedRowClassName(isHighlighted, rowClassName)}>
          <MultiComboboxItem
            isSelected={selectedItemKeys.has(keyExtractor(item))}
          >
            {contentExtractor?.(item) ?? valueExtractor(item)}
          </MultiComboboxItem>
        </div>
      )}
      keyExtractor={(item) => keyExtractor(item)}
      renderEmptyState={DefaultEmptyState}
      onLoadMore={onLoadMore}
      {...comboboxProps}
    />
  )
}

interface MultiComboboxItemProps {
  isSelected: boolean
}

function MultiComboboxItem({
  isSelected,
  children,
}: PropsWithChildren<MultiComboboxItemProps>) {
  return (
    <CheckboxLabelled
      checked={isSelected}
      className="gap-x-3 overflow-hidden"
      labelClassName="truncate"
    >
      {children}
    </CheckboxLabelled>
  )
}
